import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import logo from '../logo2.png';

const Navbar = () => {
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const [open, setOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [venturesOpen, setVenturesOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('role');
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 flex justify-between items-center px-6 py-4 shadow-md bg-white/5 backdrop-blur-md text-white border-b border-white/20">

      {/* Logo */}
      <Link to="/" className="flex items-center space-x-2">
        <img
          src={logo}
          alt="NeoMegaOne Logo"
          className="h-8 w-auto rounded-full hover:opacity-80 transition"
        />
        <span className="text-xl font-semibold text-[#1E90FF]">NeoMegaOne</span>
      </Link>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center space-x-4">
        <Link
          to="/aboutus"
          className="px-4 py-2 rounded font-semibold text-white transition bg-emerald-600 hover:bg-emerald-700 hover:opacity-90"
        >
          About Us
        </Link>

        <Link
          to="/pricing"
          className="px-4 py-2 rounded font-semibold text-white transition hover:opacity-90"
          style={{ backgroundColor: 'oklch(60% 0.25 300)' }}
        >
          Pricing
        </Link>

        {isAuthenticated ? (
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded transition"
            aria-label="Logout"
          >
            Logout
          </button>
        ) : (
          <Link
            to="/login"
            className="bg-[#1E90FF] hover:bg-blue-700 px-4 py-2 rounded transition"
            aria-label="Login"
          >
            Login
          </Link>
        )}

        {/* Desktop Ventures Dropdown */}
        <div className="relative">
          <button
            onClick={() => setVenturesOpen(!venturesOpen)}
            className="flex items-center px-4 py-2 rounded font-semibold text-white transition bg-amber-500 hover:bg-amber-600 hover:opacity-90"
          >
            Studio
            <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${venturesOpen ? 'rotate-180' : ''}`} />
          </button>

          {venturesOpen && (
            <div className="absolute left-0 mt-2 w-48 bg-[#0B0F2F]/95 backdrop-blur-md rounded-xl shadow-2xl border border-white/20 z-50 p-2 overflow-hidden">
              <a href="https://caaalmly.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 rounded-lg transition group/item">
                <div className="w-2 h-2 rounded-full bg-blue-400 group-hover/item:scale-125 transition"></div>
                <div>
                  <p className="text-sm font-bold">Caaalmly</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-tighter">Ecommerce</p>
                </div>
              </a>
              <a href="https://storyntale.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 rounded-lg transition group/item">
                <div className="w-2 h-2 rounded-full bg-purple-400 group-hover/item:scale-125 transition"></div>
                <div>
                  <p className="text-sm font-bold">Storyntale</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-tighter">Digital Media</p>
                </div>
              </a>
              <div className="border-t border-white/10 mt-2 pt-2">
                <Link to="/products" className="block px-4 py-2 text-xs text-[#1E90FF] hover:bg-white/10 rounded-lg text-center font-bold">
                  View All Products →
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setResourcesOpen(!resourcesOpen)}
            className="flex items-center px-3 py-2 rounded bg-white/10 hover:bg-white/20 transition"
          >
            <Menu className="h-5 w-5 mr-1" />
            <ChevronDown className="h-4 w-4" />
          </button>

          {resourcesOpen && (
            <div
              className="absolute right-0 mt-2 w-40 bg-white/10 backdrop-blur-md rounded shadow-lg border border-white/20 z-50"

            >
              <Link to="/careers" className="block px-4 py-2 text-white hover:bg-white/20">
                Careers
              </Link>
              <Link to="/resources/downloads" className="block px-4 py-2 text-white hover:bg-white/20">
                Downloads
              </Link>
              <Link to="/resources/blog" className="block px-4 py-2 text-white hover:bg-white/20">
                Blog
              </Link>
              <Link to="/resources/videos" className="block px-4 py-2 text-white hover:bg-white/20">
                Videos
              </Link>

              <Link to="/faqs" className="block px-4 py-2 text-white hover:bg-white/20">
                FAQs
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Toggle Button */}
      <button
        className="md:hidden"
        onClick={() => setOpen(!open)}
      >
        {open ? <X size={28} /> : <Menu size={28} />}
      </button>

      {/* Mobile Dropdown */}
      {open && (
        <div className="absolute top-16 right-0 bg-[#0B0F2F]/95 backdrop-blur-md py-6 shadow-lg border-l border-white/20 w-11/12 max-w-xs rounded-l-xl md:hidden">
          <ul className="flex flex-col items-center space-y-4">

            <li>
              <Link
                to="/aboutus"
                onClick={() => setOpen(false)}
                className="text-lg text-white hover:text-[#1E90FF]"
              >
                About Us
              </Link>
            </li>

            <li>
              <Link
                to="/pricing"
                onClick={() => setOpen(false)}
                className="text-lg text-white hover:text-[#1E90FF]"
              >
                Pricing
              </Link>
            </li>

            {/* Mobile Studio */}
            <li className="flex flex-col items-center w-full">
              <button
                onClick={() => setVenturesOpen(!venturesOpen)}
                className="text-lg text-white hover:text-[#1E90FF] flex items-center space-x-1"
              >
                <span>Studio</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${venturesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {venturesOpen && (
                <ul className="flex flex-col items-center mt-2 space-y-2 w-full bg-white/5 py-3 rounded-xl">
                  <li>
                    <a href="https://caaalmly.com" target="_blank" rel="noopener noreferrer" onClick={() => { setOpen(false); setVenturesOpen(false); }} className="text-white hover:text-[#1E90FF]">Caaalmly</a>
                  </li>
                  <li>
                    <a href="https://storyntale.com" target="_blank" rel="noopener noreferrer" onClick={() => { setOpen(false); setVenturesOpen(false); }} className="text-white hover:text-[#1E90FF]">Storyntale</a>
                  </li>
                  <li>
                    <Link to="/products" onClick={() => { setOpen(false); setVenturesOpen(false); }} className="text-[#1E90FF] font-bold mt-2">All Products →</Link>
                  </li>
                </ul>
              )}
            </li>

            {/* Auth */}
            <li>
              {isAuthenticated ? (
                <button
                  onClick={() => { handleLogout(); setOpen(false); }}
                  className="text-lg text-white hover:text-[#1E90FF]"
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="text-lg text-white hover:text-[#1E90FF]"
                >
                  Login
                </Link>
              )}
            </li>

            {/* ⭐ Careers ABOVE Resources */}
            <li>
              <Link
                to="/careers"
                onClick={() => setOpen(false)}
                className="text-lg text-white hover:text-[#1E90FF]"
              >
                Careers
              </Link>
            </li>

            {/* ⭐ FAQs ABOVE Resources */}
            <li>
              <Link
                to="/faqs"
                onClick={() => setOpen(false)}
                className="text-lg text-white hover:text-[#1E90FF]"
              >
                FAQs
              </Link>
            </li>

            {/* Mobile Resources */}
            <li className="flex flex-col items-center w-full">
              <button
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className="text-lg text-white hover:text-[#1E90FF] flex items-center space-x-1"
              >
                <span>Resources</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {resourcesOpen && (
                <ul className="flex flex-col items-center mt-2 space-y-2 w-full">

                  {/* ONLY these inside Resources now */}
                  <li>
                    <Link
                      to="/resources/downloads"
                      onClick={() => setOpen(false)}
                      className="text-white hover:text-[#1E90FF]"
                    >
                      Downloads
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/resources/blog"
                      onClick={() => setOpen(false)}
                      className="text-white hover:text-[#1E90FF]"
                    >
                      Blog
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/resources/videos"
                      onClick={() => setOpen(false)}
                      className="text-white hover:text-[#1E90FF]"
                    >
                      Videos
                    </Link>
                  </li>

                </ul>
              )}
            </li>

          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
